import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Play, Pin, X, RotateCcw } from 'lucide-react'

type Fund = { id: string; name: string; ticker: string; source: string }

type FactorRow = {
  factor: string
  beta: number | null; beta_ortho: number | null
  t_stat: number | null; p_value: number | null
  significant: boolean
  correlation: number | null
}

type StyleBucket = {
  bucket: string; family?: string; share: number; abs_beta_sum: number; n_factors: number
}

type FactorResp = {
  run_id: number; n_obs: number
  r_squared: number | null
  alpha_weekly: number | null
  alpha_annualized: number | null
  factors: FactorRow[]
  style_breakdown?: StyleBucket[]
  factor_families?: string[] | null
  included_factors?: string[] | null
  excluded_factors?: string[] | null
}

type FamilyInfo = { id: string; label: string }

export default function RiskFactor() {
  const [search, setSearch] = useState('')
  const [fundId, setFundId] = useState('')
  const [universe, setUniverse] = useState('narrow')
  const [window, setWindow] = useState('full')
  const [topK, setTopK] = useState(15)
  const [families, setFamilies] = useState<string[]>([])  // empty = all
  const [pinned, setPinned]     = useState<string[]>([])
  const [excluded, setExcluded] = useState<string[]>([])

  const fundsQ = useQuery<Fund[]>({
    queryKey: ['fa-funds'],
    queryFn: () => api.get('/fund-analysis/funds').then(r => r.data),
  })
  const familiesQ = useQuery<FamilyInfo[]>({
    queryKey: ['factor-families'],
    queryFn: () => api.get('/risk-stats/factor-families').then(r => r.data),
  })

  const run = useMutation<FactorResp, unknown>({
    mutationFn: () => api.post('/risk-stats/factor-model', {
      fund_id: fundId, universe, time_window: window, top_k: topK,
      factor_families: families.length > 0 ? families : null,
      included_factors: pinned.length > 0 ? pinned : null,
      excluded_factors: excluded.length > 0 ? excluded : null,
    }).then(r => r.data),
  })

  const togglePin = (factor: string) => {
    setExcluded(excluded.filter(x => x !== factor))
    setPinned(pinned.includes(factor)
      ? pinned.filter(x => x !== factor)
      : [...pinned, factor])
  }
  const toggleExclude = (factor: string) => {
    setPinned(pinned.filter(x => x !== factor))
    setExcluded(excluded.includes(factor)
      ? excluded.filter(x => x !== factor)
      : [...excluded, factor])
  }
  const clearCuration = () => {
    setPinned([])
    setExcluded([])
  }

  const filteredFunds = useMemo(() => {
    if (!fundsQ.data) return []
    const s = search.toLowerCase()
    return fundsQ.data
      .filter(f => !s || f.name?.toLowerCase().includes(s) || f.ticker?.toLowerCase().includes(s))
      .slice(0, 300)
  }, [fundsQ.data, search])

  const pct = (v: number | null | undefined, d = 2) => v == null ? '—' : `${(v * 100).toFixed(d)}%`
  const num = (v: number | null | undefined, d = 3) => v == null ? '—' : v.toFixed(d)

  const factors = useMemo(() => {
    if (!run.data?.factors) return []
    return [...run.data.factors].sort(
      (a, b) => Math.abs(b.t_stat ?? 0) - Math.abs(a.t_stat ?? 0)
    )
  }, [run.data])

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Factor Model</h1>
        <p className="text-muted-foreground">
          Orthogonalised (Gram-Schmidt / Moore-Penrose) factor model on weekly-overlapping 5-day returns, with Ridge regression, t-stats and p-values.
        </p>
      </div>

      {/* Controls */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            placeholder="Search by name or ticker..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input flex-1"
          />
          <select
            value={fundId}
            onChange={e => setFundId(e.target.value)}
            className="input w-full md:w-[28rem]"
          >
            <option value="">-- Select fund --</option>
            {filteredFunds.map(f => (
              <option key={f.id} value={f.id}>{f.name} ({f.ticker}) [{f.source}]</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Universe</span>
            <select value={universe} onChange={e => setUniverse(e.target.value)} className="input">
              <option value="narrow">narrow</option>
              <option value="wide">wide</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Window</span>
            <select value={window} onChange={e => setWindow(e.target.value)} className="input">
              <option value="full">full</option>
              <option value="10y">10y</option>
              <option value="5y">5y</option>
              <option value="2y">2y</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Top K factors</span>
            <input
              type="number"
              value={topK}
              onChange={e => setTopK(Math.max(3, Math.min(50, Number(e.target.value) || 15)))}
              className="input w-20"
            />
          </label>
          <button
            type="button"
            disabled={!fundId || run.isPending}
            onClick={() => run.mutate()}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run factor model
          </button>
        </div>

        {/* Factor family filter — restrict the universe by asset class. */}
        <div className="border-t border-border pt-3">
          <div className="flex items-center gap-2 text-sm mb-2">
            <span className="font-medium">Factor families</span>
            <span className="text-xs text-muted-foreground">
              (empty = full narrow universe; pick one or more to isolate e.g. equity-only style)
            </span>
            <button
              type="button"
              onClick={() => setFamilies([])}
              className="ml-auto text-xs text-muted-foreground hover:text-foreground underline"
            >clear</button>
            <button
              type="button"
              onClick={() => setFamilies(['equity'])}
              className="text-xs text-muted-foreground hover:text-foreground underline"
            >equity-only</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(familiesQ.data ?? []).map(f => {
              const active = families.includes(f.id)
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() =>
                    setFamilies(active
                      ? families.filter(x => x !== f.id)
                      : [...families, f.id])
                  }
                  className={
                    'text-xs px-2 py-1 rounded border transition-colors ' +
                    (active
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-muted hover:bg-accent')
                  }
                >
                  {f.label}
                </button>
              )
            })}
          </div>
        </div>
        {run.isError && (
          <div className="text-sm text-destructive">error — {String((run.error as any)?.response?.data?.detail ?? run.error)}</div>
        )}
      </div>

      {/* Summary */}
      {run.data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Metric label="R² (weekly)"       value={run.data.r_squared == null ? '—' : run.data.r_squared.toFixed(3)} />
            <Metric label="Alpha (annualised)" value={pct(run.data.alpha_annualized, 2)} />
            <Metric label="# observations"    value={String(run.data.n_obs)} />
            <Metric label="# significant (p<0.05)" value={String(run.data.factors.filter(f => f.significant).length)} />
          </div>

          {(run.data.style_breakdown?.length ?? 0) > 0 && (
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold">Investment style (share of |β<sub>ortho</sub>|)</h2>
                {run.data.factor_families && run.data.factor_families.length > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded border border-primary/40 bg-primary/10 text-primary">
                    Restricted to: {run.data.factor_families.join(' · ')}
                  </span>
                )}
              </div>
              <div className="space-y-1.5">
                {run.data.style_breakdown!.slice(0, 10).map(b => (
                  <div key={b.bucket} className="flex items-center gap-3 text-sm">
                    <div className="w-44 text-xs">{b.bucket}</div>
                    <div className="flex-1 h-3 bg-muted rounded overflow-hidden">
                      <div
                        className="h-3 bg-primary"
                        style={{ width: `${Math.round(b.share * 100)}%` }}
                      />
                    </div>
                    <div className="w-16 text-right font-mono text-xs">{(b.share * 100).toFixed(1)}%</div>
                    <div className="w-10 text-right text-xs text-muted-foreground">n={b.n_factors}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground mt-3">
                Each bucket's share = Σ|β<sub>ortho</sub>| of factors mapped to that style, normalised to 1.
                The top 2–3 buckets describe the fund's investment style.
              </div>
            </div>
          )}

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h2 className="font-semibold">Factor exposures (sorted by |t-stat|)</h2>
              <span className="text-xs text-muted-foreground">
                run_id <span className="font-mono">{run.data.run_id}</span>
              </span>
            </div>

            {/* Curation status bar — what the user has pinned / excluded, and
                a button to re-run with the curation applied. */}
            {(pinned.length > 0 || excluded.length > 0 ||
              (run.data.included_factors?.length ?? 0) > 0 ||
              (run.data.excluded_factors?.length ?? 0) > 0) && (
              <div className="mb-3 border border-border rounded p-2 bg-muted/30 flex flex-wrap gap-2 items-center text-xs">
                {pinned.length > 0 && (
                  <>
                    <span className="text-muted-foreground">Pinned:</span>
                    {pinned.map(p => (
                      <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-primary/40 bg-primary/10 text-primary font-mono">
                        <Pin className="h-3 w-3" />{p}
                        <button onClick={() => togglePin(p)} className="hover:text-destructive"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </>
                )}
                {excluded.length > 0 && (
                  <>
                    <span className="text-muted-foreground">Excluded:</span>
                    {excluded.map(e => (
                      <span key={e} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-destructive/40 bg-destructive/10 text-destructive font-mono">
                        <X className="h-3 w-3" />{e}
                        <button onClick={() => toggleExclude(e)} className="hover:text-foreground"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </>
                )}
                <button
                  onClick={clearCuration}
                  className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border hover:bg-muted"
                >
                  <RotateCcw className="h-3 w-3" /> Clear
                </button>
                <button
                  onClick={() => run.mutate()}
                  disabled={run.isPending}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary text-primary-foreground disabled:opacity-40"
                >
                  {run.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                  Re-run curated
                </button>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3">Factor</th>
                    <th className="text-right py-2 px-3">Corr</th>
                    <th className="text-right py-2 px-3">Beta (orig)</th>
                    <th className="text-right py-2 px-3">Beta (ortho)</th>
                    <th className="text-right py-2 px-3">t-stat</th>
                    <th className="text-right py-2 px-3">p-value</th>
                    <th className="text-center py-2 px-3">Curate</th>
                  </tr>
                </thead>
                <tbody>
                  {factors.map((f) => {
                    const isPinned = pinned.includes(f.factor)
                    const isExcluded = excluded.includes(f.factor)
                    return (
                    <tr
                      key={f.factor}
                      className={'border-b border-border/50 ' +
                        (isPinned ? 'bg-primary/10 '
                          : isExcluded ? 'bg-destructive/5 opacity-60 '
                          : f.significant ? 'bg-primary/5 ' : '')}
                    >
                      <td className="py-2 px-3 font-mono text-xs">{f.factor}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs">{num(f.correlation)}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs">{num(f.beta)}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs">{num(f.beta_ortho)}</td>
                      <td className={'py-2 px-3 text-right font-mono text-xs ' + (Math.abs(f.t_stat ?? 0) > 1.96 ? 'text-primary font-semibold' : '')}>
                        {num(f.t_stat, 2)}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-xs">{num(f.p_value, 4)}</td>
                      <td className="py-2 px-2 text-center whitespace-nowrap">
                        <button
                          onClick={() => togglePin(f.factor)}
                          title={isPinned ? 'Unpin (remove force-include)' : 'Pin — force this factor into the regression'}
                          className={'inline-flex items-center px-1.5 py-0.5 rounded border text-xs mr-1 ' +
                            (isPinned
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border hover:bg-muted')}
                        >
                          <Pin className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => toggleExclude(f.factor)}
                          title={isExcluded ? 'Un-exclude' : 'Exclude — drop this factor (non-causal / spurious)'}
                          className={'inline-flex items-center px-1.5 py-0.5 rounded border text-xs ' +
                            (isExcluded
                              ? 'border-destructive bg-destructive text-destructive-foreground'
                              : 'border-border hover:bg-muted')}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-muted-foreground mt-3 space-y-1">
              <div>Highlighted rows: p &lt; 0.05 (blue). <Pin className="inline h-3 w-3" /> force this factor in; <X className="inline h-3 w-3" /> drop it from the pool (non-causal / spurious pick).</div>
              <div>After curating rows, press <span className="font-medium">Re-run curated</span> at the top to rebuild the regression with your overrides. The run persists under a new run_id so the old pick remains auditable.</div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-xl font-semibold mt-1 font-mono">{value}</div>
    </div>
  )
}
